import React, { useCallback } from 'react';
import { FormattedNumber } from 'react-intl';
import { useObservable } from 'react-use-observable';

import { Modal, Button, Row, Col, Alert } from 'antd';
import { Icon } from '@eduzz/ui-antd-theme';

import { formatDate } from 'helpers/date';
import PermissionHide from 'v2/components/Shared/PermissionHide';
import type { IRefund } from 'v2/interfaces/requestManagement/refund';
import refundService from 'v2/services/requestManagement/refund';

export default function Details() {
  const [open] = useObservable(() => refundService.getDetails(), []);
  const [currentRefund] = useObservable(() => refundService.getCurrentRefund(), []);

  const closeDetails = useCallback(() => {
    refundService.closeDetails();
  }, []);

  const refundMotive = useCallback(() => {
    const refund: IRefund = { ...currentRefund };
    refundService.closeDetails();
    refundService.openReproveMotive(refund);
  }, [currentRefund]);

  const approve = useCallback(() => {
    const refund: IRefund = { ...currentRefund };
    refundService.closeDetails();
    refundService.openApprove(refund);
  }, [currentRefund]);

  return (
    <Modal
      open={open}
      width={600}
      title='Detalhes do pedido'
      onCancel={closeDetails}
      footer={
        <PermissionHide action='myeduzz:request-management:aprovar'>
          <div className='flex flex-wrap items-center justify-end gap-2'>
            <Button type='text' onClick={refundMotive} danger>
              Reprovar Reembolso
            </Button>
            <Button type='primary' onClick={approve}>
              Aprovar Reembolso
            </Button>
          </div>
        </PermissionHide>
      }
      closable
    >
      <RefundDetails currentRefund={currentRefund} />
    </Modal>
  );
}

const RefundDetails = ({ currentRefund }: { currentRefund: IRefund }) => {
  if (!currentRefund) return null;
  const isExpired = currentRefund.requested_after_deadline;

  return (
    <Row gutter={[16, 16]} align='middle'>
      <Col span={24}>
        <div className='flex flex-wrap items-center'>
          {isExpired ? (
            <Icon.CircleAlert size={28} className='mr-2 size-7 text-[1.75rem] text-(--uizz-antd-color-error)!'  />
          ) : (
            <Icon.CircleCheck size={28} className='mr-2 size-7 text-[1.75rem] text-(--uizz-antd-color-success)'  />
          )}
          <span>
            Reembolso solicitado em {formatDate(currentRefund?.created_at, 'dd/MM/yyyy')} -
            {isExpired ? <b> Fora do prazo</b> : <b> Dentro do prazo</b>}
          </span>
        </div>
      </Col>
      <Col span={24}>
        <p className='mb-1! text-base font-medium!'>Valor da compra</p>
        <p className='mb-0! text-lg!'>
          <FormattedNumber value={currentRefund?.invoice_value} format='BRL' />
        </p>
      </Col>

      <Col span={24}>
        <p className='mb-1! text-base font-medium!'>Motivo</p>
        <p className='mb-0! text-sm!'>{currentRefund?.request_motive.description}</p>
      </Col>

      {currentRefund?.description && (
        <Col span={24}>
          <p className='mb-1! text-base font-medium!'>Detalhes</p>
          <p className='mb-0! text-sm!'>{currentRefund.description}</p>
        </Col>
      )}
      <Col span={24}>
        <Alert
          type='info'
          message={
            <>
              Você tem até o dia <b>{formatDate(currentRefund?.due_date, 'dd/MM/yyyy')}</b> para aprovar ou reprovar o
              reembolso.
            </>
          }
        />
      </Col>
    </Row>
  );
};
